import { FC } from 'react'
import clsx from 'clsx'
import { Typography } from '../../../Typography'
import type { TableCellProps } from './types'
import { isCustomTableCell } from '../../typeguards/isCustomTableCell'
import { getPinningClassNames, getPinningStyles } from '../../utils/getPinning'

export const TableCell: FC<TableCellProps> = ({
  children,
  cell,
  isCondensed = false,
  isRowClickable = false,
}) => {
  const cellValue = cell.getValue()
  const { justify, showOnRowHover, transparentBackground } =
    cell.column.columnDef.meta || {}

  const isCustomCell = isCustomTableCell(cellValue)
  const customContent = isCustomCell ? cellValue.content : undefined

  const value = isCustomCell && !cellValue.content ? cellValue.value : cellValue

  if (isCustomCell && cellValue.isHidden) return null

  const isPinned = cell.column.getIsPinned()
  const isLastLeftPinnedColumn =
    isPinned === 'left' && cell.column.getIsLastColumn('left')

  // 150 is the default column width defined by TanStack. If no custom width is supplied, don't set a width.
  const width =
    cell.column.getSize() === 150 ? undefined : cell.column.getSize()

  const renderContent = () => {
    if (typeof value === 'string' || typeof value === 'number') {
      return (
        <Typography size="sm" weight="regular" className="text-default">
          {value}
        </Typography>
      )
    }

    if (
      typeof customContent === 'string' ||
      typeof customContent === 'number'
    ) {
      return (
        <Typography size="sm" weight="regular" className="text-default">
          {customContent}
        </Typography>
      )
    }

    return customContent || children
  }

  return (
    <td
      // The 'h-1' class ensures the cell's children stretch to fill its height.
      className={clsx('h-1 align-middle', getPinningClassNames(cell.column), {
        'bg-high': isPinned,
        'group-hover:bg-medium-hover': isRowClickable,
        '!bg-transparent': transparentBackground,
        'z-20': isPinned,
      })}
      style={{
        ...getPinningStyles(cell.column),
        // Min and max width are needed as the table uses auto-layout by default
        minWidth: width,
        maxWidth: width,
      }}
    >
      <div
        className={clsx('flex items-center h-full', {
          'justify-end': justify === 'right',
          'justify-center': justify === 'center',
          'border-default border-r': isLastLeftPinnedColumn,
          'p-3': isCondensed,
          'p-4': !isCondensed,
          'opacity-0': showOnRowHover,
          'group-hover:opacity-100 group-hover:visible': showOnRowHover,
        })}
      >
        {renderContent()}
      </div>
    </td>
  )
}
